import { Card, DietaryBadge, QuantityStepper } from '@catering/ui';
import type { FocusEvent, JSX, KeyboardEvent } from 'react';
import { useRef, useState } from 'react';

import { AvailableMenu } from './api';

export interface MenuSelectorProps {
  menus: AvailableMenu[];
  /** Keyed by `positionId` (`rotating-menu/design.md` §5) — one row per position, never per `menuId`. */
  quantities: Record<string, number>;
  onQuantityChange: (positionId: string, quantity: number) => void;
  /** Optional per-line note (RF-06-10) — omitted entirely when the caller does not need it. */
  notes?: Record<string, string>;
  onNotesChange?: (positionId: string, notes: string) => void;
}

interface ServiceTypeGroup {
  code: string;
  name: string;
  menus: { menu: AvailableMenu; index: number }[];
}

/** Groups menus by `serviceType.code`, preserving each group's first-appearance order (the API already sorts by `displayOrder`, then incoming order). */
function groupByServiceType(menus: AvailableMenu[]): ServiceTypeGroup[] {
  const groups = new Map<string, ServiceTypeGroup>();

  menus.forEach((menu, index) => {
    const group = groups.get(menu.serviceType.code);

    if (group === undefined) {
      groups.set(menu.serviceType.code, {
        code: menu.serviceType.code,
        name: menu.serviceType.name,
        menus: [{ menu, index }],
      });
    } else {
      group.menus.push({ menu, index });
    }
  });

  return Array.from(groups.values());
}

/**
 * `portal-orders/spec.md` → "Alta completa por teclado" (RF-06-11): every
 * quantity input is a native `<input>`, so `Tab` already moves between
 * them in document order. `Enter` additionally moves focus to the next
 * line's input instead of doing nothing (or submitting a form it is not
 * inside), so a user can complete five lines without ever reaching for
 * the mouse. `QuantityStepper` (`@catering/ui`) does not expose an `id`
 * or `onKeyDown` prop, so the wrapper `div` around each stepper carries
 * the keyboard handling and its `onKeyDown` catches the bubbled event
 * from the nested native input.
 */
export function MenuSelector({
  menus,
  quantities,
  onQuantityChange,
  notes,
  onNotesChange,
}: MenuSelectorProps): JSX.Element {
  const [expandedNotes, setExpandedNotes] = useState<ReadonlySet<string>>(
    () =>
      new Set(
        menus
          .filter((menu) => (notes?.[menu.positionId]?.length ?? 0) > 0)
          .map((menu) => menu.positionId),
      ),
  );
  const stepperWrappers = useRef<Array<HTMLDivElement | null>>([]);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>, index: number): void {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    stepperWrappers.current[index + 1]?.querySelector('input')?.focus();
  }

  function handleFocus(event: FocusEvent<HTMLDivElement>): void {
    // A keyboard-only pass (RF-06-11) needs `Tab`ing into a field whose
    // default is 0 to just type over it, not append after it.
    if (event.target instanceof HTMLInputElement) {
      event.target.select();
    }
  }

  function expandNotes(positionId: string): void {
    setExpandedNotes((current) => new Set(current).add(positionId));
  }

  function renderMenuCard(menu: AvailableMenu, index: number): JSX.Element {
    // Once a note field has been shown (expanded, or with text at
    // mount), it stays shown: clearing an in-progress note must not
    // unmount the focused input.
    const noteVisible =
      notes !== undefined && onNotesChange !== undefined && expandedNotes.has(menu.positionId);

    return (
      <Card key={menu.positionId} variant="outlined" className="flex flex-col gap-3 p-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
              {menu.position.name}
            </span>
            <span className="text-sm font-medium text-slate-900">{menu.name}</span>
            {menu.description !== null ? (
              <span className="text-xs text-slate-500">{menu.description}</span>
            ) : null}
            {menu.dietaryTags.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {menu.dietaryTags.map((tag) => (
                  <DietaryBadge key={tag} code={tag} />
                ))}
              </div>
            ) : null}
            {menu.availableQuantity !== null ? (
              <span className="text-xs text-slate-500">
                Cupo disponible: {menu.availableQuantity}
              </span>
            ) : null}
          </div>

          <div
            ref={(el) => {
              stepperWrappers.current[index] = el;
            }}
            onKeyDown={(event) => handleKeyDown(event, index)}
            onFocus={handleFocus}
          >
            <QuantityStepper
              value={quantities[menu.positionId] ?? 0}
              onChange={(value) => onQuantityChange(menu.positionId, value)}
              label={`Cantidad de ${menu.name}`}
              {...(menu.availableQuantity !== null ? { max: menu.availableQuantity } : {})}
            />
          </div>
        </div>

        {notes !== undefined && onNotesChange !== undefined ? (
          noteVisible ? (
            <input
              type="text"
              aria-label={`Observaciones de ${menu.name}`}
              placeholder="Observaciones"
              value={notes[menu.positionId] ?? ''}
              onChange={(event) => onNotesChange(menu.positionId, event.target.value)}
              className="h-11 w-full rounded-md border border-slate-300 px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
            />
          ) : (
            <button
              type="button"
              onClick={() => expandNotes(menu.positionId)}
              className="self-start text-xs font-medium text-primary-700 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
            >
              Agregar observación a {menu.name}
            </button>
          )
        ) : null}
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {groupByServiceType(menus).map((group) => (
        <div key={group.code} className="flex flex-col gap-3">
          <h2 className="text-sm font-semibold text-slate-900">{group.name}</h2>
          {group.menus.map(({ menu, index }) => renderMenuCard(menu, index))}
        </div>
      ))}
    </div>
  );
}
